/**
 * mix_math.ts — levels, ducking and the environment snapshot (2026-09-20). PURE.
 *
 * DUCKING IS ARBITRATED, NEVER STACKED. Several things may ask a bus to make
 * room at once (a versona speaking, a cinematic line, a live voice-chat peer).
 * Adding their attenuations makes the game disappear; the rule here is the
 * STRONGEST request per bus, capped — and a request is a TOKEN that ends on its
 * own clock, so a cancelled utterance can never leave the music ducked.
 *
 * USER GAIN, SNAPSHOT GAIN, DUCK GAIN AND THE MUTE GATE ARE SEPARATE STAGES.
 * One shared gain would let a cutscene exit or a duck release overwrite the
 * volume a player chose.
 */
import type { Bus } from './buses.js';

export const dbToGain = (db: number): number => (db <= -120 ? 0 : Math.pow(10, db / 20));
export const gainToDb = (g: number): number => (g <= 1e-6 ? -120 : 20 * Math.log10(g));

/** A slider's 0..1 → gain. Squared: the top half of a linear fader is nearly inaudible change. */
export function volumeToGain(v: number): number { const c = Math.min(1, Math.max(0, v)); return c * c; }

export type DuckProfile = Partial<Record<Bus, number>>; // bus → attenuation in dB (negative)

/** What SPEECH asks of the mix (the report's proposed starting ranges, conservative end). */
export const DUCK_SPEECH: DuckProfile = { music: -7, ambience: -4, weather: -4, combat: -2, vehicles: -3, machinery: -2 };
/** A live voice-chat peer talking: lighter — a call must not pump the world on every syllable. */
export const DUCK_VOICECHAT: DuckProfile = { music: -4, ambience: -2, weather: -2 };
/** Never duck a bus further than this, whatever asks. */
export const DUCK_FLOOR_DB = -12;
export const DUCK_ATTACK_S = 0.06;
export const DUCK_RELEASE_S = 0.5;

export interface DuckRequest { tag: string; profile: DuckProfile; untilS: number }

/** The gain each bus should sit at right now: the strongest live request per bus, floored. */
export function duckGains(requests: Iterable<DuckRequest>, nowS: number): Partial<Record<Bus, number>> {
  const db: Partial<Record<Bus, number>> = {};
  for (const r of requests) {
    if (r.untilS <= nowS) continue;
    for (const [bus, att] of Object.entries(r.profile) as Array<[Bus, number]>) {
      const cur = db[bus] ?? 0;
      if (att < cur) db[bus] = Math.max(DUCK_FLOOR_DB, att);
    }
  }
  const out: Partial<Record<Bus, number>> = {};
  for (const [bus, d] of Object.entries(db) as Array<[Bus, number]>) out[bus] = dbToGain(d);
  return out;
}

// ─── The environment snapshot ────────────────────────────────────────────────

export type ReverbKind = 'none' | 'room' | 'cave' | 'hall';

export interface EnvInput { submerged: boolean; indoors: boolean; inCave: boolean; spaceFlight: boolean; aboardShip: boolean }

export interface EnvSnapshot {
  /** Low-pass over the WORLD sub-mix (Hz). Under water everything physical is dull; warnings and speech are not in this mix. */
  worldCutoffHz: number;
  /** Low-pass over the weather bus: rain heard through a roof loses its hiss, not just its level. */
  weatherCutoffHz: number;
  reverb: ReverbKind;
  /** Wet return level (linear). Conservative — a tail should be noticed when it is gone, not when it is there. */
  reverbWet: number;
}

export function envSnapshot(e: EnvInput): EnvSnapshot {
  if (e.submerged) return { worldCutoffHz: 650, weatherCutoffHz: 400, reverb: 'none', reverbWet: 0 };
  if (e.spaceFlight) return { worldCutoffHz: 20000, weatherCutoffHz: 20000, reverb: 'none', reverbWet: 0 };
  if (e.inCave) return { worldCutoffHz: 20000, weatherCutoffHz: 900, reverb: 'cave', reverbWet: 0.34 };
  if (e.aboardShip) return { worldCutoffHz: 20000, weatherCutoffHz: 700, reverb: 'hall', reverbWet: 0.26 };
  if (e.indoors) return { worldCutoffHz: 20000, weatherCutoffHz: 1400, reverb: 'room', reverbWet: 0.22 };
  return { worldCutoffHz: 20000, weatherCutoffHz: 20000, reverb: 'none', reverbWet: 0 };
}

/** Impulse-response recipes (procedural — no asset): seconds of tail, damping of the highs, pre-delay. */
export const REVERB_RECIPES: Record<Exclude<ReverbKind, 'none'>, { seconds: number; damping: number; preDelayMs: number; density: number }> = {
  room: { seconds: 0.55, damping: 0.55, preDelayMs: 6, density: 1 },
  hall: { seconds: 1.3, damping: 0.4, preDelayMs: 14, density: 0.9 },
  cave: { seconds: 2.2, damping: 0.3, preDelayMs: 24, density: 0.75 },
};
